import { atom } from 'jotai';
import type { Workspace } from '@/types/workspace';
import type { Contact } from '@/types/contact';
import type { UseBulkSelectionReturn } from '@/hooks/useBulkSelection';

// Contacts list state — persists across navigation
export const contactsListAtom = atom<{
  contacts: Contact[];
  totalCount: number;
  currentPage: number;
  filtersKey: string; // To detect when filters change
}>({
  contacts: [],
  totalCount: 0,
  currentPage: 1,
  filtersKey: '',
});

// Route of the list surface the user most recently viewed (/contacts or
// /filters/:id). The call panel's "Next" returns here when it can't advance,
// so a saved-filter session doesn't get dumped into the unfiltered list.
export const lastListRouteAtom = atom<string>('/contacts');

// Applied filters — persists across navigation
export const contactsFiltersAtom = atom<any>({});

// Scroll position — persists across navigation
export const contactsScrollPositionAtom = atom<number>(0);

// Core workspace atoms
export const workspacesAtom = atom<Workspace[]>([]);
export const activeWorkspaceAtom = atom<string | null>(null);
export const workspaceLoadingAtom = atom(true);

// Derived workspace atom
export const currentWorkspaceAtom = atom((get) => {
  const workspaces = get(workspacesAtom);
  const activeId = get(activeWorkspaceAtom);
  return workspaces.find(w => w.id === activeId) || null;
});

// Campaign wizard modal state
export interface CampaignWizardState {
  open: boolean;
  selectedContacts: Contact[];
  bulkSelection?: UseBulkSelectionReturn;
}

export const campaignWizardAtom = atom<CampaignWizardState>({
  open: false,
  selectedContacts: [],
});

// Call settings — workspace-level timer thresholds
export interface CallThreshold {
  minutes: number;
  color: string;
}

export const DEFAULT_CALL_THRESHOLDS: CallThreshold[] = [
  { minutes: 0, color: '#22c55e' },
  { minutes: 3, color: '#eab308' },
  { minutes: 5, color: '#f97316' },
  { minutes: 10, color: '#ef4444' },
];

export const callSettingsAtom = atom<CallThreshold[]>(DEFAULT_CALL_THRESHOLDS);